import { useState, useEffect, FC } from "react";
import moment from "moment";
import { Event } from "../../types/index";
import { BtnLoader, CollapseIcon, CopyLink, Dot } from "../../../../icons";
import Button from "../../../common/button";
import {
  RupeesIcon,
  Ticket,
  Clock,
  CalenderMinimal,
} from "../../../../icons/tags-icons";
import { useAppDispatch, useAppSelector } from "../../../../hooks/store-hooks";
import { setStep } from "../../../../store/slices/eventSlice";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../../constants/routes";
import Modal from "../../../common/modals";
import TourCard from "./success_ticket";
import { toastError, toastInfo } from "../../../toast";
import { getLocalStorage } from "../../../../helpers/storage";
import Cookies from "js-cookie";
import { useCreateOrderMutation } from "../../../../store/api/orderApi";
import { useLazyGetEventByIdQuery } from "../../../../store/api/eventApi";

interface EventInfobarProps {
  event: Event;
  isOpen: boolean;
  onClose: () => void;
  isMyEvent: boolean;
  fetchEvents: () => void;
}

const EventInfobar: FC<EventInfobarProps> = ({
  event,
  isOpen,
  onClose,
  fetchEvents,
}) => {
  const { response } = useAppSelector((state) => state.event);
  const isMyEvent = !response?.events?.some(
    (myEvent: Event) => myEvent.id === event.id,
  );
  const user = JSON.parse(getLocalStorage("userData") || "{}");

  const [isVisible, setIsVisible] = useState(true);

  const [showTicket, setShowTicket] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [triggerCreateOrder, { isLoading }] = useCreateOrderMutation();

  const [trigger] = useLazyGetEventByIdQuery();

  const [isClicked, setIsClicked] = useState(false);
  const [enteredEmail, setEnteredEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);

  const handleBack = () => {
    navigate(ROUTES.EVENTS);
    setIsVisible(false);
    dispatch(setStep(1));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setEnteredEmail(email);
    setIsEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  };

  const handleRegister = async () => {
    if (
      event.event_type === "ONLINE" &&
      !event.is_paid &&
      !user.email &&
      !enteredEmail
    ) {
      setIsClicked(true);
      return;
    }
    setIsClicked(false);
    if (event.is_paid) {
      if (!event.sold_out) {
        const refreshToken = Cookies.get("refreshToken");
        const accessToken = Cookies.get("accessToken");

        const data = {
          accessToken: accessToken,
          refreshToken: refreshToken,
        };

        const newTab = window.open(
          `${process.env.REACT_APP_PAYMENT_BASE_URL}/events/${event.id}`,
          "_blank",
        );

        if (newTab) {
          let attempts = 0;
          const maxAttempts = 6;
          const interval = setInterval(() => {
            if (!newTab.closed) {
              newTab.postMessage(
                data,
                `${process.env.REACT_APP_PAYMENT_BASE_URL}`,
              );
              clearInterval(interval);
            }
            attempts++;
            if (attempts > maxAttempts) {
              clearInterval(interval);
            }
          }, 500);
        }
      }
    } else {
      triggerCreateOrder({
        first_name: user?.first_name,
        last_name: user?.last_name,
        phone: user?.phone,
        email: user?.email || enteredEmail,
        entity_id: event.id,
        entity_type: "event",
      })
        .unwrap()
        .then(() => {
          toastInfo({
            message: "Successfully registered for the event!",
          });
          if (
            new Date(event.start_time).getTime() - new Date().getTime() >
            10 * 60 * 1000
          ) {
            handleViewTicket();
          } else {
            trigger({ id: event.id }).then(
              (result: { data: { zoom_join_url?: string } }) => {
                const eventData = result.data;
                if (eventData?.zoom_join_url) {
                  window.location.href = eventData.zoom_join_url;
                } else {
                  toastError({
                    message: "No join URL found for this event.",
                  });
                }
              },
            );
          }
          fetchEvents();
        })
        .catch((error: any) => {
          toastError({
            message: error.message || "Failed to register for the event.",
          });
        });
    }
  };

  const handleViewTicket = () => {
    setShowTicket(true);
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/events/${event.id}`;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toastInfo({ message: "Link copied to clipboard!" });
      })
      .catch((err) => {
        toastError({ message: err });
      });
  };

  const handleJoinWebinar = async () => {
    if (!isMyEvent) {
      await handleRegister();
    } else {
      if (event?.zoom_join_url) {
        window.location.href = event.zoom_join_url;
      } else {
        toastError({ message: "No join URL found for this event." });
      }
    }
  };

  useEffect(() => {
    if (!isVisible) {
      const timer = setTimeout(onClose, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="medium"
        customClass="!p-0 !text-white !h-screen !overflow-auto !bg-black text-white my-[0px]"
        variant="right"
      >
        <div className="flex justify-between items-center px-6 py-4 sticky top-0 !bg-black z-10 text-white">
          <div
            className="flex items-center cursor-pointer"
            onClick={handleBack}
          >
            <CollapseIcon width={19} height={19} />
          </div>
          <div
            className="flex items-center cursor-pointer"
            onClick={handleCopyLink}
          >
            <CopyLink />
            <span className="ml-2">Copy Link</span>
          </div>
        </div>

        <hr className="border-gray-600" />

        <div className="overflow-y-auto py-4 px-8 space-y-4  h-[calc(100%-76px)] pb-16 bg-black">
          <img
            src={event.banner}
            alt={event.title}
            className="w-full h-64 mb-4 "
          />

          <h2 className="text-2xl font-bold mt-2">{event.title}</h2>
          <div className="flex flex-row mt-3">
            <div className="flex justify-center items-center rounded-[22px] px-[8px] py-[6px] bg-[#262626] border border-[#404040] w-24">
              <Dot
                width={8}
                height={8}
                fillColor={
                  event.event_type === "ONLINE" ? "#6EE755" : "#ECD996"
                }
              />
              <div className="ml-[4px] text-xs text-white">
                {event.event_type === "ONLINE" ? "Online" : "Offline"}
              </div>
            </div>
            <div className="flex justify-center items-center rounded-[22px] px-[8px] py-[6px] bg-[#262626] border border-[#404040] w-24 ml-2">
              <div className="ml-[4px] text-xs text-white">
                {event.is_paid ? "Paid Event" : "Free Event"}
              </div>
            </div>
          </div>

          <div className="flex flex-col text-white space-y-2">
            <div className="flex flex-row space-x-2">
              <div className="flex flex-row w-1/2 bg-[#262626] border border-[#404040] p-3 rounded-[8px]">
                <div className="mr-2">
                  <CalenderMinimal width={24} height={24} />
                </div>
                <div className="text-white text-md font-normal">
                  {moment(event.start_time).format("D MMM YYYY, ddd")}
                </div>
              </div>
              <div className="flex flex-row w-1/2 bg-[#262626] border border-[#404040] p-3 rounded-[8px]">
                <div className="mr-2">
                  <Clock width={24} height={24} />
                </div>
                <div className="text-white text-md font-normal">
                  {`${moment(event.start_time).format("h:mm A")} - ${moment(
                    event.end_time,
                  ).format("h:mm A")}, ${moment(event.end_time).diff(
                    moment(event.start_time),
                    "hours",
                  )} hrs`}
                </div>
              </div>
            </div>

            <div className="flex flex-row space-x-2">
              <div className="flex flex-row w-1/2 bg-[#262626] border border-[#404040] p-3 rounded-[8px]">
                <div className="mr-2">
                  <RupeesIcon width={24} height={24} />
                </div>
                <div className="text-white text-md font-normal">
                  ₹
                  {user?.memberships?.is_opc
                    ? ((event.prices[0].member_price || 0) / 100).toFixed(2) +
                      " for members"
                    : ((event.prices[0].current_price || 0) / 100).toFixed(2) +
                      " for non-members"}
                </div>
              </div>
              <div className="flex flex-row w-1/2 bg-[#262626] border border-[#404040] p-3 rounded-[8px] space-x-2">
                <div className="mr-1">
                  <Ticket />
                </div>
                <div className="text-white text-md font-normal">
                  {event.total_tickets_sold} Registrations
                </div>
              </div>
            </div>
          </div>
          <div className="px-2 py-2">
            <p className="font-bold text-lg mb-2">About the Event</p>
            <div className="text-sm whitespace-pre-line">
              {event.description}
            </div>
          </div>
        </div>

        <div
          className={`sticky bottom-[8px] px-8 py-4 bg-[rgba(9, 9, 9, 0.42)] backdrop-blur-[21.7px] ${
            new Date(event.end_time) < new Date() ? "hidden" : ""
          } ${isMyEvent && "hidden"}`}
        >
          <Button
            customClasses="w-full rounded-xl text-white"
            onClick={
              new Date(event.start_time) > new Date()
                ? () => handleRegister()
                : handleJoinWebinar
            }
            disabled={isLoading}
          >
            {isLoading ? (
              <BtnLoader />
            ) : event.sold_out ? (
              "Registrations closed"
            ) : new Date(event.start_time).getTime() - new Date().getTime() <
                10 * 60 * 1000 && event.event_type === "ONLINE" ? (
              "Join Now"
            ) : (
              "Register"
            )}
          </Button>
        </div>
        {isMyEvent && (
          <div
            className={`sticky bottom-[8px] px-8 py-4 bg-[rgba(9, 9, 9, 0.42)] backdrop-blur-[21.7px]`}
          >
            <Button
              customClasses="w-full rounded-xl text-white"
              onClick={
                new Date(event.start_time).getTime() - new Date().getTime() <
                10 * 60 * 1000
                  ? handleJoinWebinar
                  : handleViewTicket
              }
            >
              {new Date(event.start_time).getTime() - new Date().getTime() <
              10 * 60 * 1000
                ? "Join Now"
                : "View ticket"}
            </Button>
          </div>
        )}
      </Modal>
      {showTicket && <TourCard eventData={event} isCoinSelected={false} />}

      {isClicked && (
        <Modal isOpen customClass="!w-[500px]" size="small">
          <div className=" p-16 py-12relative flex flex-col md:gap-y-4 gap-y-2 py-6 overflow-y-auto">
            <div>
              <div className="flex justify-center">
                <img
                  src="assets/event/no-email.png"
                  alt="Event"
                  className="md:w-[260px] md:h-[260px] w-[200px] h-[200px] rounded-lg"
                />
              </div>
              <button
                onClick={() => setIsClicked(false)}
                className="absolute top-2 right-2 text-gray-400"
              >
                <span className="text-xl">✕</span>
              </button>
            </div>

            <div className="md:text-[32px] text-[24px] font-bold">
              Enter your email
            </div>
            <div className="flex justify-between items-start">
              <div className=" md:text-[17px] text-[14px] max-w-[90%] leading-relaxed">
                Please add your email to register for this event. We'll send you
                important updates and confirmations here.
              </div>
            </div>

            <div className="relative">
              <input
                type="email"
                className={`w-full p-3 border ${
                  isEmailValid ? "focus:border-[#666]" : "border-red-500"
                } rounded-lg transition-colors outline-none`}
                placeholder="Enter your email address"
                onChange={handleEmailChange}
              />
              {!isEmailValid && enteredEmail && (
                <div className="text-red-500 text-sm mt-1">
                  Please enter a valid email address.
                </div>
              )}
            </div>

            <button
              className={`w-full p-4 ${
                isEmailValid
                  ? "bg-[#007AFF] hover:bg-[#0066CC]"
                  : "bg-gray-400 cursor-not-allowed"
              } transition-colors rounded-lg font-bold text-white text-base`}
              onClick={isEmailValid ? () => handleRegister() : undefined}
              disabled={!isEmailValid}
            >
              Continue
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};
export default EventInfobar;
